import { Accordion, Form } from 'react-bootstrap'
import { AccountIntegration } from '../listings/utilities/listings.interfaces'
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../common/common.entities'
import { useCallback, useMemo, useState } from 'react'
import useCustomPagination from '../common/components/CustomPagination'

interface AccountsTableProps {
  alwaysOpen: boolean
  accounts: AccountIntegration[]
  environment: string
}

export default function AccountsTable(props: AccountsTableProps){
  const {alwaysOpen, accounts, environment} = props

  const [filtered, setFiltered]: [AccountIntegration[], any] = useState([])
  const [filterText, setFilterText]: [string, any] = useState('')

  const {paginatedItems, paginationControls} = useCustomPagination<AccountIntegration>({
    pageSize: 25,
    sourceItems: filtered.length > 0 ? filtered : accounts
  })

  const BOOL_OPTIONS = [
    <option value='all'>ALL</option>,
    <option value='yes'>YES</option>,
    <option value='no'>NO</option>
  ] 

  const handleBoolFilter = useCallback((e) => {
    const {field} = e.currentTarget.dataset
    if(field.includes('.')){
      const split = field.split('.')
      const parentObject = split[0]
      const childField = split[1]
      if(e.target.value === 'all'){
        setFiltered([])
        setFilterText('')
      }else if(e.target.value === 'yes'){
        const filtered = accounts.filter(a => a[parentObject][childField] === true)
        setFiltered(filtered)
        setFilterText(`${filtered.length} account(s) filtered where ${parentObject}.${childField} is true.`)
      } else if(e.target.value === 'no'){
        const filtered = accounts.filter(a => a[parentObject][childField] === false)
        setFiltered(filtered)
        setFilterText(`${filtered.length} account(s) filtered where ${parentObject}.${childField} is false.`)
      }
    } else {
      if(e.target.value === 'all'){
        setFiltered([])
        setFilterText('')
      }else if(e.target.value === 'yes'){
        const filtered = accounts.filter(a => a[field] === true)
        setFiltered(filtered)
        setFilterText(`${filtered.length} account(s) filtered where ${field} is true.`)
      } else if(e.target.value === 'no'){
        const filtered = accounts.filter(a => a[field] === false)
        setFiltered(filtered)
        setFilterText(`${filtered.length} account(s) filtered where ${field} is true.`)
      }
    }
  }, [accounts])

  const handleFedexAccountChanged = useCallback((e) => {
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText('')
    } else {
      const filtered = accounts.filter(a => a.Fedex_Account_Number__c === e.target.value)
      setFiltered(filtered)
      setFilterText(`${filtered.length} account(s) filtered where the FedEx Account Number is ${e.target.value}.`)
    }
  }, [accounts])

  const handleUPSAccountChanged = useCallback((e) => {
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText('')
    } else {
      const filtered = accounts.filter(a => a.UPS_Account_Number__c === e.target.value)
      setFiltered(filtered)
      setFilterText(`${filtered.length} account(s) filtered where the UPS Account Number is ${e.target.value}.`)
    }
  }, [accounts])

  const handleCountryChanged = useCallback((e) => {
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText('')
    } else {
      const filtered = accounts.filter(a => a.Order_Integration__r.Country__c === e.target.value)
      setFiltered(filtered)
      setFilterText(`${filtered.length} account(s) filtered where the Integration Country is ${e.target.value}.`)
    }
  }, [accounts])

  const handleHasFieldValue = useCallback((e) => {
    const {field} = e.currentTarget.dataset
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText('')
    } else if(e.target.value === 'yes'){
      const filtered = accounts.filter(a => a[field] !== null)
      setFiltered(filtered)
      setFilterText(`${filtered.length} account(s) filtered where ${field} has a value.`)
    } else if(e.target.value === 'no'){
      const filtered = accounts.filter(a => a[field] === null)
      setFiltered(filtered)
      setFilterText(`${filtered.length} account(s) filtered where ${field} has no value.`)
    }
  }, [accounts])


  const view = useMemo(() => {
    const uniqueFedex = []
    const uniqueUps = []
    const uniqueCountry = []
    
    for(const account of accounts){
      if(account.Fedex_Account_Number__c && !uniqueFedex.includes(account.Fedex_Account_Number__c)){
        uniqueFedex.push(account.Fedex_Account_Number__c)
      }
      if(account.UPS_Account_Number__c && !uniqueUps.includes(account.UPS_Account_Number__c)){
        uniqueUps.push(account.UPS_Account_Number__c)
      }
      if(account.Order_Integration__r?.Country__c && !uniqueCountry.includes(account.Order_Integration__r.Country__c)){
        uniqueCountry.push(account.Order_Integration__r.Country__c)
      }
    }
    return <div>
      <Accordion alwaysOpen={alwaysOpen} className='custom-accordion'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header className='sticky-row-2'>
            <span className='beacon-header'>SALES ACCOUNTS [{accounts?.length ?? 0}]</span>
          </Accordion.Header>
          <Accordion.Body>
            <div>
              <table className='flexport-table'>
                <thead className='sticky-row-3'>
                  <tr>
                    <td colSpan={50}>
                      <Accordion>
                        <Accordion.Item eventKey='0'>
                          <Accordion.Header><span className='beacon-header'>FILTERS</span></Accordion.Header>
                          <Accordion.Body>
                            <table>
                              <tbody>
                                <tr>
                                  <td>
                                    <span className='beacon-label'>3<sup>rd</sup> PARTY SHIP BILLING</span>
                                    <Form.Select data-field='Third_Party_Ship_Billing__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>SHIPWISE ENABLED</span>
                                    <Form.Select data-field='DesktopShipperEnabled__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>PACK-TO-SUIT ENABLED</span>
                                    <Form.Select data-field='Pack_to_Suit_Enabled__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>REQUIRES SHIP NOTICE</span>
                                    <Form.Select data-field='Requires_Ship_Notice__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>BACKORDERS ENABLED</span>
                                    <Form.Select data-field='Enable_Backorders__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>AUTO-CREATE LISTINGS</span>
                                    <Form.Select data-field='Order_Integration__r.Autocreate__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>AUTO-ENABLE LISTINGS</span>
                                    <Form.Select data-field='Order_Integration__r.Auto_Enable_Listings__c' onChange={handleBoolFilter}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <span className='beacon-label'>FEDEX ACCOUNT</span>
                                    <Form.Select onChange={handleFedexAccountChanged}>
                                      <option value='all'>ALL</option>
                                      {uniqueFedex.map(u => (
                                        <option value={u}>{u}</option>
                                      ))}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>UPS ACCOUNT</span>
                                    <Form.Select onChange={handleUPSAccountChanged}>
                                      <option value='all'>ALL</option>
                                      {uniqueUps.map(u => (
                                        <option value={u}>{u}</option>
                                      ))}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>HAS INVOICING</span>
                                    <Form.Select data-field='Hours_To_Invoice__c' onChange={handleHasFieldValue}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>HAS WAYFAIR RETAILER ID</span>
                                    <Form.Select data-field='Wayfair_Retailer_Id__c' onChange={handleHasFieldValue}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>HAS WAYFAIR SHIPPING ACCOUNT ID</span>
                                    <Form.Select data-field='Wayfair_Shipping_Account_Number__c' onChange={handleHasFieldValue}>
                                      {BOOL_OPTIONS}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <span className='beacon-label'>INTEGRATION COUNTRY</span>
                                    <Form.Select onChange={handleCountryChanged}>
                                      <option value='all'>ALL</option>
                                      {uniqueCountry.map(u => (
                                        <option value={u}>{u}</option>
                                      ))}
                                    </Form.Select>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <span className='beacon-label'>{filterText}</span>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <span className='beacon-header'>NAME</span>
                    </th>
                    <th>
                      <span className='beacon-header'>ACCOUNT CODE</span>
                    </th>
                    <th>
                      <span className='beacon-header'>3<sup>rd</sup> PARTY SHIP BILLING</span>
                    </th>
                    <th>
                      <span className='beacon-header'>FEDEX ACCOUNT</span>
                    </th>
                    <th>
                      <span className='beacon-header'>UPS ACCOUNT</span>
                    </th>
                    <th>
                      <span className='beacon-header'>INVOICE HOURS</span>
                    </th>
                    <th>
                      <span className='beacon-header'>WF RETAILER ID</span>
                    </th>
                    <th>
                      <span className='beacon-header'>WF SHIPPING ACCT ID</span>
                    </th>
                    <th>
                      <span className='beacon-header'>SHIPWISE ENABLED</span>
                    </th>
                    <th>
                      <span className='beacon-header'>PACK TO SUIT ENABLED</span>
                    </th>
                    <th>
                      <span className='beacon-header'>REQUIRES SHIP NOTICE</span>
                    </th>
                    <th>
                      <span className='beacon-header'>BACKORDERS ENABLED</span>
                    </th>
                    <th>
                      <span className='beacon-header'>INTEGRATION</span>
                    </th>
                    <th>
                      <span className='beacon-header'>LISTING SUFFIX</span>
                    </th>
                    <th>
                      <span className='beacon-header'>AUTO-CREATE LISTINGS</span>
                    </th>
                    <th>
                      <span className='beacon-header'>AUTO-ENABLE LISTINGS</span>
                    </th>
                    <th>
                      <span className='beacon-header'>INTEGRATION COUNTRY</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedItems?.map((a, index) => (
                    <tr>
                      <td className='beacon-header'><a title='Opens the Account in Mission Control.' className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT[environment]}/${a.Id}`}>{a.Name}</a></td>
                      <td>{a.AccountCode__c}</td>
                      <td>{a.Third_Party_Ship_Billing__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Fedex_Account_Number__c}</td>
                      <td>{a.UPS_Account_Number__c}</td>
                      <td>{a.Hours_to_Invoice__c}</td>
                      <td>{a.Wayfair_Retailer_Id__c}</td>
                      <td>{a.Wayfair_Shipping_Account_Number__c}</td>
                      <td>{a.DesktopShipperEnabled__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Pack_to_Suit_Enabled__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Requires_Ship_Notice__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Enable_Backorders__c === true ? 'YES' : 'NO'}</td>
                      <td><a title='Opens the Integration in Mission Control.' className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT[environment]}/${a.Order_Integration__c}`}>{a.Order_Integration__r.Name}</a></td>
                      <td>{a.Order_Integration__r?.Listing_Name_Suffix__c}</td>
                      <td>{a.Order_Integration__r?.Autocreate__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Order_Integration__r?.Auto_Enable_Listings__c === true ? 'YES' : 'NO'}</td>
                      <td>{a.Order_Integration__r?.Country__c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className='sticky-bottom-element'>
                {paginationControls}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  }, [props, paginatedItems, paginationControls, filterText])

  return view
}